import { observable, action } from 'mobx';
import { FormikHelpers } from 'formik';
import { RootStore } from './rootStore';
import { SingleValueResponse } from 'common/interfaces';
import { transformToFormErrors } from 'helpers/helpers';

interface CustomSubmitOptions<T> {
  formikHelpers?: FormikHelpers<T>;
  singleValueResponse?: boolean;
  reset?: boolean;
  contentType?: string;
  validationPrefixDepth?: number;

}

interface CustomFetchOptions {
  resetData?: boolean;
}

interface BackendHelpers {
  options?: object;
  defaultData?: object;
}

export class FormStore<T> {
  rootStore: RootStore;
  accountId = '';

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @observable data: T | null = null;

  @action setData = (data: T) => (this.data = data);

  @action resetData = () => {
    this.data = null;
  };

  fetchData = async (endpoint: string, customOptions?: CustomFetchOptions) => {
    const defaultOptions = {
      resetData: true,
    };
    const options = Object.assign({}, defaultOptions, customOptions);

    if (options.resetData) {
      this.resetData();
    }

    const { apiStore } = this.rootStore.stores;

    const response = await apiStore.get<T>(endpoint);

    if (response?.data) {
      const { data } = response;
      this.setData(data);
    }
  };

  setErrors = <T>(errors: { key: string }[], formikHelpers: FormikHelpers<T>, validationPrefixDepth: number = 1) => {
    const formikErrors = transformToFormErrors(errors, validationPrefixDepth);
    for (let [key, value] of Object.entries(formikErrors)) {
      formikHelpers.setFieldError(key, value);
    }
  };

  submitData = async <P>(
    endpoint: string,
    data: P & BackendHelpers,
    customOptions?: Partial<CustomSubmitOptions<T>>,
  ) => {
    const defaultOptions = {
      formikHelpers: undefined as FormikHelpers<T> | undefined,
      reset: false,
    };

    const options = Object.assign({}, defaultOptions, customOptions);

    if (options.reset) {
      this.resetData();
    }

    const { post } = this.rootStore.stores.apiStore;

    // destructure options and make an object from the rest, so the prop options insn't sent
    const { options: filterdOptions, defaultData, ...filteredData } = data;

    //const formFields = flatten(filteredData, options.validationPrefixDepth);
    //console.log(formFields);

    const response = await post<SingleValueResponse<boolean> | T>(endpoint, filteredData, false, false);

    options.formikHelpers && options.formikHelpers.setSubmitting(false);

    if (response) {
      const { errors, data } = response;

      if (options && options.formikHelpers) {
        // if there is key errors that came from the backend, it will contain validation errors
        if (errors) {
          this.setErrors(errors, options.formikHelpers, options.validationPrefixDepth);
          return false;
        }
      }
      if (data) {
        if (!options.singleValueResponse) {
          this.setData(response.data as T);
          return true;
        }
        if (options.singleValueResponse && (data as SingleValueResponse<boolean>).value) {
          return true;
        }
      } else {
        console.error(`Something went wrong saving to ${endpoint}`);
        return false;
      }
    }
    return false;
  };
}
